'use client'

import React, { useCallback, useMemo } from 'react'
import { toast } from '@kiao-voice/ui-kit'
import { useTranslation } from 'react-i18next'
import { readStoredToken } from '../auth/auth-storage'
import {
  fetchInvoiceSettings,
  guestCheckoutViaTappay,
  saveInvoiceSettings,
  subscribeViaTappay,
} from './api-client'
import { CheckoutDialog } from './checkout/checkout-dialog'
import {
  createGuestCheckoutStrategy,
  createSubscribeStrategy,
} from './checkout/checkout-strategies'
import type { PersonalPaidPlanCode, SubscriptionCycle } from '@kiao-voice/core'

/**
 * 'authed' — signed-in checkout via /billing/subscribe (default).
 * 'guest' — token-less checkout from /pricing: the cardholder email becomes
 * the account email and the public /billing/guest-checkout endpoint
 * find-or-creates + charges. An existing account short-circuits to
 * `onRequiresSignIn` (OTP pivot) without charging; a charge that settles
 * without a session for this device reports it with `paid` set.
 */
export type CheckoutMode = 'authed' | 'guest'

interface SubscribeCheckoutModalProps {
  open: boolean
  planCode: PersonalPaidPlanCode
  cycle: SubscriptionCycle
  /** Charge amount in TWD, shown on the confirm button + amount line. */
  amount: number
  /** Credits granted by the plan — for the success toast copy. */
  monthlyCredits: number
  /** Logged-in user's email, to pre-fill the cardholder field. */
  userEmail: string | null
  mode?: CheckoutMode
  /** Authed mode only: org wallet (儲值金) balance → offset breakdown + card-free
   *  path. Null in guest mode (a brand-new account has no wallet). */
  wallet?: { enabled: boolean; total: number } | null
  /** Present when a successful paid checkout immediately replaces a gift plan. */
  replacingComplimentary?: boolean
  /** Guest mode only: the email already has an account — caller pivots to OTP. */
  onRequiresSignIn?: (email: string, paid: boolean) => void
  onClose: () => void
  onSuccess?: () => void
  /** Test seams. */
  getTokenFn?: () => string | null
  subscribeFn?: typeof subscribeViaTappay
  guestCheckoutFn?: typeof guestCheckoutViaTappay
  invoiceLoadFn?: typeof fetchInvoiceSettings
  invoiceSaveFn?: typeof saveInvoiceSettings
  redirect?: (url: string) => void
  skipScript?: boolean
}

/**
 * In-place subscription checkout used by `/pricing` so a signed-in OR signed-out
 * visitor can subscribe without leaving the page. A thin wrapper over the shared
 * `<CheckoutDialog>`/`<CheckoutPanel>` (issue #249): it picks the subscribe vs
 * token-less guest strategy and supplies the pricing surface's copy + test hooks;
 * the breakdown, card form, NT$0 path, and every server-outcome mapping live in
 * the shared panel. The 3DS branch is a real bank redirect, so it navigates away
 * by design; the non-3DS happy path stays on `/pricing`.
 */
export function SubscribeCheckoutModal({
  open,
  planCode,
  cycle,
  amount,
  monthlyCredits,
  userEmail,
  mode = 'authed',
  wallet = null,
  replacingComplimentary = false,
  onRequiresSignIn,
  onClose,
  onSuccess,
  getTokenFn = readStoredToken,
  subscribeFn = subscribeViaTappay,
  guestCheckoutFn = guestCheckoutViaTappay,
  invoiceLoadFn = fetchInvoiceSettings,
  invoiceSaveFn = saveInvoiceSettings,
  redirect,
  skipScript,
}: SubscribeCheckoutModalProps) {
  const { t } = useTranslation('web')

  const strategy = useMemo(
    () =>
      mode === 'guest'
        ? createGuestCheckoutStrategy({ planCode, cycle, amount, guestCheckoutFn, t })
        : createSubscribeStrategy({ planCode, cycle, amount, subscribeFn, getTokenFn, t }),
    [mode, planCode, cycle, amount, guestCheckoutFn, subscribeFn, getTokenFn, t]
  )

  // The success toast lives here (the panel hands success back to the container):
  // close happened first, so the toast is no longer behind the dialog.
  const handleSuccess = useCallback(() => {
    toast.success(t('billing.toastSubscribed', { credits: monthlyCredits }))
    onSuccess?.()
  }, [t, monthlyCredits, onSuccess])

  const planName = t(
    planCode === 'creator' ? 'billing.planCardCreatorName' : 'billing.planCardProName'
  )

  return (
    <CheckoutDialog
      visible={open}
      onClose={onClose}
      title={
        mode === 'guest' ? t('billing.modalGuestCheckoutTitle') : t('billing.modalSubscribeTitle')
      }
      subtitle={t('billing.modalSummarySubscribe', {
        plan: planName,
        cycle: cycle === 'yearly' ? t('billing.cycleYearly') : t('billing.cycleMonthly'),
      })}
      dialogTestId="pricing-checkout-modal"
      closeTestId="pricing-checkout-close"
      amount={amount}
      // Guest accounts have no wallet; only authed gets the offset + card-free path.
      wallet={mode === 'authed' ? wallet : null}
      cardFree={mode === 'authed'}
      coupon={{ product: 'subscription', planCode, cycle }}
      userEmail={mode === 'guest' ? null : userEmail}
      strategy={strategy}
      note={mode === 'guest' ? t('billing.guestPayNote') : t('billing.payNote')}
      summaryNote={
        replacingComplimentary ? (
          <p className="alert show" data-testid="pricing-complimentary-conversion-note">
            {t('billing.complimentaryConversionNotice')}
          </p>
        ) : undefined
      }
      testIdPrefix="pricing"
      formTestId="pricing-subscribe-form"
      // Guest only: the cardholder email becomes the account email.
      emailHint={mode === 'guest' ? t('billing.guestEmailHint') : undefined}
      resetKey={`${mode}-${planCode}-${cycle}`}
      onSuccess={handleSuccess}
      onRequiresSignIn={onRequiresSignIn}
      redirect={redirect}
      skipScript={skipScript}
      // Invoice fields are authed-only: a guest has no saved settings yet and the
      // load would 401 (the account doesn't exist until the charge creates it).
      invoiceLoadFn={mode === 'authed' ? invoiceLoadFn : undefined}
      invoiceSaveFn={mode === 'authed' ? invoiceSaveFn : undefined}
    />
  )
}
